/**
 * CoordinateErrorClassification.ts
 * Sistema tipado de clasificación de errores de coordenadas PTEL.
 *
 * Define 5 categorías de error (E1-E5) detectables en documentos PTEL
 * de Andalucía, con severidad, autocorrección y metadatos diagnósticos.
 *
 * Alineado con el patrón `kind` discriminant de GeocodingErrors.ts.
 *
 * @module CoordinateErrorClassification
 * @version 1.0.0
 * @date Febrero 2026
 */

// ============================================================================
// ENUMS Y CONSTANTES
// ============================================================================

/**
 * Categorías de error de coordenadas.
 * E1-E5 cubren los problemas más frecuentes en documentos PTEL.
 */
export type CoordinateErrorKind =
  | 'truncated_y'       // E1: Y truncada (falta prefijo "4" en Andalucía)
  | 'european_decimal'   // E2: Separador decimal europeo (coma)
  | 'wrong_datum_ed50'   // E3: Datum ED50 en lugar de ETRS89
  | 'swapped_xy'         // E4: X e Y intercambiadas
  | 'out_of_bounds';     // E5: Fuera del rango de Andalucía

/**
 * Severidad del error respecto al impacto en geolocalización.
 * - critical: coordenada inutilizable sin corrección
 * - degraded: coordenada utilizable pero con desviación significativa
 * - warning:  posible problema, requiere verificación
 */
export type CoordinateErrorSeverity = 'critical' | 'degraded' | 'warning';

/**
 * Confianza en la autocorrección aplicada.
 * - high:   corrección determinista (ej: añadir "4" a Y de 6 dígitos)
 * - medium: corrección probable (ej: transformación ED50→ETRS89)
 * - low:    corrección especulativa (ej: swap X/Y ambiguo)
 */
export type CorrectionConfidence = 'high' | 'medium' | 'low';

// ============================================================================
// INTERFAZ BASE
// ============================================================================

/**
 * Coordenada cruda (antes de clasificación).
 * Usa `number | string` porque el input puede llegar con coma decimal.
 */
export interface RawCoordinate {
  /** Valor X (easting UTM o longitud) */
  readonly x: number | string;
  /** Valor Y (northing UTM o latitud) */
  readonly y: number | string;
  /** Sistema de referencia declarado (si se conoce) */
  readonly declaredCrs?: string;
  /** Código INE del municipio (5 dígitos) para validación geográfica */
  readonly ine5?: string;
}

/**
 * Resultado de clasificación de una coordenada.
 * Puede tener 0..N errores detectados simultáneamente.
 */
export interface CoordinateClassificationResult {
  /** Coordenada original analizada */
  readonly raw: RawCoordinate;
  /** Errores detectados (vacío = coordenada limpia) */
  readonly errors: readonly CoordinateError[];
  /** Coordenada corregida (si todos los errores son autocorregibles) */
  readonly corrected: CorrectedCoordinate | null;
  /** ¿Todos los errores detectados fueron autocorregidos? */
  readonly allFixed: boolean;
  /** Puntuación 0-100 de confianza global post-corrección */
  readonly confidenceScore: number;
}

// ============================================================================
// ERRORES DISCRIMINADOS (Unión discriminada por `kind`)
// ============================================================================

/** Interfaz base compartida por todos los errores de coordenadas */
interface CoordinateErrorBase {
  /** Discriminador — identifica el tipo de error */
  readonly kind: CoordinateErrorKind;
  /** Severidad del error */
  readonly severity: CoordinateErrorSeverity;
  /** Mensaje legible para log/UI */
  readonly message: string;
  /** ¿Se puede autocorregir? */
  readonly autoFixable: boolean;
  /** Confianza de la autocorrección (solo si autoFixable) */
  readonly correctionConfidence?: CorrectionConfidence;
}

// ---------------------------------------------------------------------------
// E1 — Coordenada Y truncada
// ---------------------------------------------------------------------------

/**
 * E1: La coordenada Y (northing UTM30) tiene menos dígitos de los esperados.
 * Muy frecuente en PTEL andaluces: "100234" en lugar de "4100234".
 * En Andalucía, Y UTM30 siempre empieza por "4" (rango ~4050000-4290000).
 */
export interface TruncatedYError extends CoordinateErrorBase {
  readonly kind: 'truncated_y';
  readonly severity: 'critical';
  readonly autoFixable: true;
  readonly correctionConfidence: 'high';
  /** Valor Y original (truncado) */
  readonly originalY: number;
  /** Dígitos que tiene */
  readonly digitCount: number;
  /** Dígitos esperados (7 para UTM30 Andalucía) */
  readonly expectedDigits: 7;
  /** Prefijo inferido ("4") */
  readonly inferredPrefix: '4';
  /** Valor Y corregido */
  readonly correctedY: number;
}

// ---------------------------------------------------------------------------
// E2 — Formato decimal europeo
// ---------------------------------------------------------------------------

/**
 * E2: La coordenada usa coma como separador decimal (formato europeo).
 * Ejemplo: "456123,45" en lugar de "456123.45".
 */
export interface EuropeanDecimalError extends CoordinateErrorBase {
  readonly kind: 'european_decimal';
  readonly severity: 'critical';
  readonly autoFixable: true;
  readonly correctionConfidence: 'high';
  /** Eje afectado */
  readonly axis: 'x' | 'y' | 'both';
  /** Valor(es) original(es) como string con coma */
  readonly originalValues: { x?: string; y?: string };
  /** Valor(es) corregido(s) como number */
  readonly correctedValues: { x?: number; y?: number };
}

// ---------------------------------------------------------------------------
// E3 — Datum ED50
// ---------------------------------------------------------------------------

/**
 * E3: La coordenada está en datum ED50, no en ETRS89.
 * La transformación ED50→ETRS89 introduce un desplazamiento de ~200 m.
 */
export interface WrongDatumED50Error extends CoordinateErrorBase {
  readonly kind: 'wrong_datum_ed50';
  readonly severity: 'degraded';
  readonly autoFixable: true;
  readonly correctionConfidence: 'medium';
  /** Coordenadas en ED50 */
  readonly ed50: { x: number; y: number };
  /** Coordenadas transformadas a ETRS89 */
  readonly etrs89: { x: number; y: number };
  /** Desplazamiento aplicado (metros) */
  readonly shiftMeters: { dx: number; dy: number };
}

// ---------------------------------------------------------------------------
// E4 — X e Y intercambiadas
// ---------------------------------------------------------------------------

/**
 * E4: X e Y están intercambiadas.
 * Detección heurística: si X cae en rango Y y viceversa.
 */
export interface SwappedXYError extends CoordinateErrorBase {
  readonly kind: 'swapped_xy';
  readonly severity: 'critical';
  readonly autoFixable: true;
  readonly correctionConfidence: 'low';
  /** Valores originales (en orden incorrecto) */
  readonly original: { x: number; y: number };
  /** Valores intercambiados (orden correcto) */
  readonly swapped: { x: number; y: number };
}

// ---------------------------------------------------------------------------
// E5 — Fuera de rango Andalucía
// ---------------------------------------------------------------------------

/**
 * E5: La coordenada (incluso tras correcciones) queda fuera de Andalucía.
 * Rango UTM30 ETRS89 Andalucía:
 *   X: ~100.000 – 620.000
 *   Y: ~4.050.000 – 4.290.000
 */
export interface OutOfBoundsError extends CoordinateErrorBase {
  readonly kind: 'out_of_bounds';
  readonly severity: 'critical';
  readonly autoFixable: false;
  /** Coordenadas evaluadas */
  readonly coordinates: { x: number; y: number };
  /** Eje(s) fuera de rango */
  readonly outOfRangeAxis: ('x' | 'y')[];
  /** Distancia al borde más cercano de Andalucía (metros, estimada) */
  readonly distanceToBorder?: number;
}

// ============================================================================
// UNIÓN DISCRIMINADA
// ============================================================================

/**
 * Unión discriminada de los 5 tipos de error de coordenadas.
 * Usar `error.kind` para narrowing en switch/if.
 */
export type CoordinateError =
  | TruncatedYError
  | EuropeanDecimalError
  | WrongDatumED50Error
  | SwappedXYError
  | OutOfBoundsError;

// ============================================================================
// COORDENADA CORREGIDA
// ============================================================================

/**
 * Coordenada tras aplicar autocorrecciones.
 */
export interface CorrectedCoordinate {
  /** X corregida (UTM30 ETRS89) */
  readonly x: number;
  /** Y corregida (UTM30 ETRS89) */
  readonly y: number;
  /** CRS de salida (siempre EPSG:25830) */
  readonly crs: 'EPSG:25830';
  /** Correcciones aplicadas, en orden */
  readonly appliedFixes: readonly CoordinateErrorKind[];
  /** Confianza mínima entre todas las correcciones */
  readonly minConfidence: CorrectionConfidence;
}

// ============================================================================
// RANGOS DE REFERENCIA ANDALUCÍA (UTM30 ETRS89)
// ============================================================================

/** Rango válido X (easting) para UTM30 Andalucía */
export const ANDALUCIA_X_RANGE = { min: 100_000, max: 620_000 } as const;

/** Rango válido Y (northing) para UTM30 Andalucía */
export const ANDALUCIA_Y_RANGE = { min: 4_050_000, max: 4_290_000 } as const;

/** Dígitos esperados para Y (northing) UTM30 en Andalucía */
export const EXPECTED_Y_DIGITS = 7 as const;

/** Prefijo Y para Andalucía */
export const ANDALUCIA_Y_PREFIX = '4' as const;

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isTruncatedYError(e: CoordinateError): e is TruncatedYError {
  return e.kind === 'truncated_y';
}

export function isEuropeanDecimalError(e: CoordinateError): e is EuropeanDecimalError {
  return e.kind === 'european_decimal';
}

export function isWrongDatumED50Error(e: CoordinateError): e is WrongDatumED50Error {
  return e.kind === 'wrong_datum_ed50';
}

export function isSwappedXYError(e: CoordinateError): e is SwappedXYError {
  return e.kind === 'swapped_xy';
}

export function isOutOfBoundsError(e: CoordinateError): e is OutOfBoundsError {
  return e.kind === 'out_of_bounds';
}

// ============================================================================
// METADATOS DE ERROR (para UI/reporting)
// ============================================================================

/** Mapa de código → metadatos para UI y reporting */
export const ERROR_METADATA: Record<CoordinateErrorKind, {
  readonly code: string;
  readonly label: string;
  readonly description: string;
  readonly icon: string;
}> = {
  truncated_y: {
    code: 'E1',
    label: 'Y truncada',
    description: 'Coordenada Y incompleta (falta prefijo "4")',
    icon: '✂️',
  },
  european_decimal: {
    code: 'E2',
    label: 'Decimal europeo',
    description: 'Separador decimal con coma en lugar de punto',
    icon: '🔄',
  },
  wrong_datum_ed50: {
    code: 'E3',
    label: 'Datum ED50',
    description: 'Coordenada en ED50, requiere transformación a ETRS89',
    icon: '🌐',
  },
  swapped_xy: {
    code: 'E4',
    label: 'X/Y invertidas',
    description: 'Easting y northing intercambiados',
    icon: '↔️',
  },
  out_of_bounds: {
    code: 'E5',
    label: 'Fuera de rango',
    description: 'Coordenada fuera del ámbito de Andalucía',
    icon: '📍',
  },
} as const;
